import { GlobalStyles } from '@mui/material';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import i18n from 'i18next';
import React, { createContext, useEffect, useState } from 'react';
import { initReactI18next, useTranslation } from 'react-i18next';
import { useLocation, Outlet, useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';

import AppBar from '../components/app-bar';
import Copyright from '../components/copyright';
import Nav from '../components/drawer/nav';
import { useAuthContext } from '../contexts/authContext';
import { CompanyAreaProvider } from '../contexts/CompaniesAreasContext';
import { TEntities } from '../helpers/entities';
import cs from '../locales/cs.json';
import en from '../locales/en.json';

import './layout.scss';
import './icons.scss';
import 'react-toastify/dist/ReactToastify.min.css';

type TAlerts = {
  crudStatus: 'clone' | 'create' | 'update' | 'delete';
  systemStatus: 'success' | 'info' | 'warning' | 'error';
};

type TDelete = {
  id: string;
  name: string;
  type?: string | undefined;
  deviceTypeId?: string;
};

type TContext = {
  handleDelete: (id: string, name: string, type?: string, deviceTypeId?: string) => void;
  selectedItem: TDelete;
  setSelectedItem: React.Dispatch<React.SetStateAction<TDelete>>;
  smartBarActive: boolean;
  setSmartBarActive: React.Dispatch<React.SetStateAction<boolean>>;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  toggle: boolean;
  setToggle: React.Dispatch<React.SetStateAction<boolean>>;
  changeToggle: () => void;
  systemAlert: (status: TAlerts['systemStatus'] | 'error', text: string) => void;
  crudAlert: (
    status: TAlerts['crudStatus'],
    entity: TEntities['entitySingular'],
    gender: 'default' | 'f' | 'm',
    name?: string
  ) => void;
};

export const AppContext: React.Context<TContext | null> = createContext<TContext | null>(null);

void i18n
  .use(initReactI18next) // passes i18n down to react-i18next
  .init({
    resources: { en, cs },
    lng: localStorage.getItem('lng') || 'en', // Use 'en' if no value is found
    interpolation: {
      escapeValue: false, // React already safes from XSS
    },
  });

function Layout(): JSX.Element {
  const { t, i18n } = useTranslation();
  const location = useLocation();

  const toggleValue: boolean = localStorage.getItem('toggleSet') === 'true';
  const [toggle, setToggle] = useState<boolean>(toggleValue);

  const [selectedItem, setSelectedItem] = useState<TDelete>({ id: '', name: '' });
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const [smartBarActive, setSmartBarActive] = useState<boolean>(false);

  const { user } = useAuthContext();

  const handleDelete = (id: string, name: string, type?: string, deviceTypeId?: string): void => {
    if (type && deviceTypeId) {
      setSelectedItem({ id: id, name: name, type: type, deviceTypeId: deviceTypeId });
    } else {
      setSelectedItem({ id: id, name: name });
    }
    setIsOpen(true);
  };

  useEffect(() => {
    const storedLanguage = localStorage.getItem('lng') || 'en';

    i18n.changeLanguage(storedLanguage).catch((error) => console.error('Error changing language:', error));
  }, []);

  const changeToggle = (): void => {
    const newValue = !toggle;
    setToggle(newValue);
    localStorage.setItem('toggleSet', newValue.toString());
  };

  const globalStyles = {
    '.MuiDataGrid-root': {
      backgroundColor: 'white',
    },
    '.MuiDataGrid-columnHeader': {
      backgroundColor: '#e7e7e7 !important',
      color: '#3a3a3a',
    },
    '.MuiDataGrid-columnHeader span.MuiButtonBase-root svg.MuiSvgIcon-root': {
      color: '#3a3a3a',
    },
    '.MuiDataGrid-columnHeaderTitle': {
      fontWeight: '600 !important',
      fontSize: '13px',
    },
    '.MuiDataGrid-row .MuiDataGrid-cell:not(:last-child)': {
      borderRight: '1px solid #e1e1e1 !important',
    },
    '.grid-odd': {
      background: '#f5f5f5',
    },
    '.row-selected': {
      background: '#dcf0ff',
    },
    '.MuiDataGrid-root a.MuiButton-contained': {
      paddingTop: '7px',
    },
    '.MuiDataGrid-root a.MuiButton-contained svg': {
      marginBottom: '3px',
    },
    '.MuiDataGrid-root a.MuiButton-outlined': {
      paddingTop: '7px',
    },
    '.MuiDataGrid-root a.MuiButton-outlined svg': {
      marginBottom: '3px',
    },
    '.MuiStepContent-root': {
      marginLeft: '17px !important',
      paddingLeft: '30px',
    },
    '.MuiStepConnector-root': {
      marginLeft: '17px !important',
    },
    '.MuiStepIcon-text': {
      fontWeight: '600',
      fontSize: '11px !important',
    },
  };

  const makeAlert = (
    entity: TEntities['entitySingular'],
    status: TAlerts['crudStatus'],
    gender: 'default' | 'f' | 'm',
    name?: string
  ): JSX.Element => {
    const translatedEntity = t(`entities.singular.${entity}`);
    return (
      <>
        {status === 'clone' && <>{`${translatedEntity} ${t(`validation.confirm.cloned.${gender}`)}`}</>}
        {status === 'create' && name && (
          <>
            {translatedEntity} <b>{name}</b> {t(`validation.confirm.created.${gender}`)}
          </>
        )}
        {status === 'update' && name && (
          <>
            {translatedEntity} <b>{name}</b> {t(`validation.confirm.updated.${gender}`)}
          </>
        )}
        {status === 'delete' && (
          <>
            {translatedEntity} {t(`validation.confirm.deleted.${gender}`)}
          </>
        )}
      </>
    );
  };

  const systemAlert = (status: TAlerts['systemStatus'], text: string): void => {
    switch (status) {
      case 'success':
        toast.success(text);
        break;
      case 'info':
        toast.info(text);
        break;
      case 'warning':
        toast.warning(text);
        break;
      case 'error':
        toast.error(text);
        break;
      default:
        break;
    }
  };

  const crudAlert = (
    status: TAlerts['crudStatus'],
    entity: TEntities['entitySingular'],
    gender: 'default' | 'f' | 'm',
    name?: string
  ): void => {
    switch (status) {
      case 'clone':
        toast.success(makeAlert(entity, status, gender));
        break;
      case 'create':
        toast.success(makeAlert(entity, status, gender, name));
        break;
      case 'update':
        toast.success(makeAlert(entity, status, gender, name));
        break;
      case 'delete':
        toast.success(makeAlert(entity, status, gender));
        break;
      default:
        break;
    }
  };

  const navigate = useNavigate();

  if (user && user.isPasswordChangeRequired) {
    navigate(`/reset-password/${encodeURIComponent(user.email.toString())}`);
  }

  return (
    <Box>
      <GlobalStyles styles={globalStyles} />
      <CssBaseline />
      <AppContext.Provider
        value={{
          handleDelete,
          selectedItem,
          setSelectedItem,
          smartBarActive,
          setSmartBarActive,
          isOpen,
          setIsOpen,
          toggle,
          setToggle,
          changeToggle,
          systemAlert,
          crudAlert,
        }}
      >
        <ToastContainer
          position="top-center"
          autoClose={3500}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          pauseOnHover
          theme="light"
          stacked
        />
        <CompanyAreaProvider>
          <>
            <AppBar />
            <Nav />
            <Box
              component="main"
              sx={{
                backgroundColor: (theme) =>
                  theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[900],
                flexGrow: 1,
                display: 'flex',
                position: 'relative',
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  pl: '65px',
                  ...(location.pathname !== '/map' && {
                    pl: { xs: '57px', sm: '64px' },
                    ...(toggle && {
                      pl: { xs: '220px', sm: '219px' },
                    }),
                  }),
                }}
              >
                <Outlet />
                {location.pathname.includes('/settings') ? <></> : <Copyright />}
              </Box>
            </Box>
          </>
        </CompanyAreaProvider>
      </AppContext.Provider>
    </Box>
  );
}

export default Layout;
